import { useRef } from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { gsap, useGSAP, MOTION_OK } from "../gsap";
import { Frame } from "./Frame";
import { digitalWorks, INSTAGRAM_URL } from "../content";

// Alternating vertical offsets break the row into a hung gallery wall.
const offsets = ["md:-translate-y-[5vh]", "md:translate-y-[6vh]", "md:-translate-y-[2vh]", "md:translate-y-[4vh]"];

export function Digital() {
  const wrap = useRef<HTMLElement>(null);
  const track = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`(min-width: 768px) and ${MOTION_OK}`, () => {
        const el = track.current!;
        const distance = () => el.scrollWidth - window.innerWidth;
        gsap.set(wrap.current, { overflowX: "hidden" });
        // Vertical scroll walks the viewer along the wall, one piece at a time.
        const pan = gsap.to(el, {
          x: () => -distance(),
          ease: "none",
          scrollTrigger: {
            trigger: wrap.current,
            start: "top top",
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });
        gsap.utils.toArray<HTMLElement>(".pan-piece").forEach((piece) => {
          gsap.fromTo(
            piece.querySelector("img"),
            { xPercent: -3, scale: 1.07 },
            {
              xPercent: 3,
              scale: 1.07,
              ease: "none",
              scrollTrigger: {
                trigger: piece,
                containerAnimation: pan,
                start: "left right",
                end: "right left",
                scrub: true,
              },
            },
          );
        });
      });
    },
    { scope: wrap },
  );

  return (
    <section ref={wrap} id="digital" className="no-scrollbar relative bg-coal md:overflow-x-auto [--outline-fill:var(--color-coal)]">
      <div
        ref={track}
        className="md:flex md:h-[100dvh] md:w-max md:items-center md:gap-[7vw] md:px-[8vw]"
      >
        <header className="px-4 pb-10 pt-20 md:w-[42vw] md:shrink-0 md:p-0">
          <h2 className="font-display text-[clamp(2.25rem,4.6vw,5rem)] font-extrabold uppercase leading-[0.92] tracking-[-0.04em]">
            Drawn in <br />
            <span className="text-outline">the dark</span>
          </h2>
          <p className="mt-8 max-w-[42ch] text-base leading-relaxed text-smoke">
            A crest for every class. Digital illustration in engraved linework, each one crowned with dice.
          </p>
        </header>

        {/* Mobile: a swipeable row with the next piece peeking in. Desktop: the
            wrapper dissolves and the pieces join the pinned track. */}
        <div className="no-scrollbar flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-20 md:contents">
        {digitalWorks.map((piece, i) => (
          <figure
            key={piece.title}
            className={`pan-piece shrink-0 snap-start ${offsets[i % offsets.length]}`}
            style={{ width: `min(78vw, ${Math.round((60 * piece.width) / piece.height)}vh)` }}
          >
            <Frame piece={piece} />
            <figcaption className="mt-4 flex items-baseline justify-between gap-4 px-1.5">
              <span className="font-display text-lg font-bold uppercase tracking-tight">{piece.title}</span>
              <span className="text-sm text-smoke">{piece.medium}</span>
            </figcaption>
          </figure>
        ))}

        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex w-[70vw] shrink-0 snap-start flex-col justify-center gap-6 md:w-[34vw]"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full ring-1 ring-white/20 transition-[background-color,color,rotate] duration-700 ease-heavy group-hover:rotate-45 group-hover:bg-stab group-hover:text-ink">
            <ArrowUpRight weight="light" size={28} />
          </span>
          <span className="font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tighter md:text-[3.2vw]">
            The rest lives on Instagram
          </span>
        </a>
        </div>
      </div>
    </section>
  );
}
